import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Button as MuiButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import IdentityBulletItem from "../IdentityBulletItem";
import AppDialog from "../../apps/AppDialog";
import UploadIdentityForm from "../UploadIdentityForm";
import { useQueryProfile } from "../../../graphql/apollo/useQuery";
import { useDispatch } from "../../../store/store";
import { profileActions } from "../../../store/slices/profileSlice";
import { IdentityVerifyIcon } from "../../../assets/svg/IdentifyVerifiyIcon";
import { IDENTITY_STATUS } from "../../../constants/constants";
import { IdCardSvg } from "../../../assets/svg/IdCardSvg";

interface StepProps {
  backToProfile: () => void;
}

const IdentityVerificationStep: React.FC<StepProps> = ({ backToProfile }) => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const { t } = useTranslation();
  const handleClose = () => setOpenDialog(!openDialog);
  const onInitSteps = (data: any) => {
    const { myProfile } = data;
    dispatch(
      profileActions.initStep({
        isVerifiedEmail: myProfile.isVerifiedEmail,
        isUpdatePersonalData: myProfile.isUpdatePersonalData,
        isVerifiedIdentity: myProfile.isVerifiedIdentity,
      })
    );
  };
  const { myProfile } = useQueryProfile((data: any) => onInitSteps(data));
  const isVerify = myProfile?.isVerifiedIdentity;
  const isPending = myProfile?.identityDoc?.status === IDENTITY_STATUS.PENDING;

  return (
    <div className="verification-step">
      <AppDialog
        fullScreen
        open={openDialog}
        onClose={handleClose}
        title={t("proofOfIdentity")}
      >
        <UploadIdentityForm handleClose={handleClose} />
      </AppDialog>
      <Typography variant="h5" sx={{ textAlign: "center" }}>
        {t("proofOfIdentity")}
      </Typography>
      {isPending ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ textAlign: "center", mb: 3 }} color="green">
            {t("verifyPending")}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IdCardSvg />
          </Box>
          <MuiButton
            variant="text"
            color="warning"
            fullWidth
            sx={{ mt: 2 }}
            onClick={backToProfile}
          >
            {t("backToProfile")}
          </MuiButton>
        </Box>
      ) : isVerify ? (
        <>
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ textAlign: "center", mb: 3 }} color="green">
              {t("verifySuccessfully")}
            </Typography>
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <IdentityVerifyIcon />
            </Box>
            <MuiButton
              variant="text"
              color="warning"
              fullWidth
              sx={{ mt: 2 }}
              onClick={backToProfile}
            >
              {t("backToProfile")}
            </MuiButton>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body1">{t("proofOfIdentityInfo")}</Typography>
          <Typography sx={{ mt: 2, mb: 1 }} variant="body2">
            {t("docToShow")}
          </Typography>
          <Grid container>
            <Grid item xs={6}>
              <IdentityBulletItem text={t("yourFullname")} />
              <IdentityBulletItem text={t("yourPhoto")} />
              <IdentityBulletItem text={t("dateOfBirth")} />
            </Grid>
            <Grid item xs={6}>
              <IdentityBulletItem text={t("expiryDate")} />
              <IdentityBulletItem text={t("documentnumber")} />
              <IdentityBulletItem text={t("yourSignature")} />
            </Grid>
          </Grid>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "30px",
              marginBottom: "10px",
            }}
          >
            <Button
              variant="contained"
              color="warning"
              onClick={() => setOpenDialog(true)}
            >
              {t("uploadDocuments")}
            </Button>
          </div>

          <Typography
            variant="body2"
            sx={{ textAlign: "center", fontSize: "12px" }}
          >
            {t("upload")}
          </Typography>
        </>
      )}
    </div>
  );
};

export default IdentityVerificationStep;
